import { useEffect, useRef } from 'react';

/**
 * Reading-progress rail across the top of the viewport.
 *
 * Writes a transform directly to the node on a rAF-throttled scroll listener
 * rather than going through React state, so a fast scroll cannot queue a render
 * per frame.
 */
export function ScrollProgress() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let ticking = false;

    const update = () => {
      ticking = false;
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      const ratio = max > 0 ? Math.min(1, doc.scrollTop / max) : 0;
      el.style.transform = `scaleX(${ratio})`;
      el.style.opacity = ratio > 0.005 ? '1' : '0';
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div
      ref={ref}
      className="scroll-progress"
      aria-hidden="true"
      style={{ transform: 'scaleX(0)', opacity: 0, transition: 'opacity .3s ease' }}
    />
  );
}
